import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import {
  Button,
  Callout,
  Dialog,
  DialogText,
  FieldLabel,
  RadioCards,
  Select,
  TextArea,
  TextInput,
  formatDateTime,
  isApiError,
} from "@amber/ui";
import {
  turnConflictSchema,
  type CorrectionChange,
  type EvidenceRecord,
  type SpeakerView,
  type TurnConflict,
  type TurnView,
} from "../../contracts/index";
import { OpenReports, communicationOf, type Editing } from "./Editing";

function speakerLabel(s: SpeakerView): string {
  if (s.kind === "unresolved") return s.display;
  return `${s.display}${speakerSuffix(s)}`;
}

function speakerSuffix(s: SpeakerView): string {
  if (s.kind === "tracked") return " (tracked)";
  return s.profile ? " (not tracked)" : " (no profile)";
}

interface Draft {
  text: string;
  speakerId: SpeakerView["id"] | null;
  confirm: boolean;
  reason: string;
}

function SpeakerRow({
  speakers,
  value,
  onChange,
}: {
  speakers: readonly SpeakerView[];
  value: SpeakerView["id"] | null;
  onChange: (speakerId: SpeakerView["id"] | null) => void;
}) {
  return (
    <div className="mb-1 flex flex-wrap items-center gap-2">
      <Select
        aria-label="Speaker for this turn"
        value={value ?? ""}
        onChange={(e) => {
          onChange(speakers.find((x) => x.id === e.target.value)?.id ?? null);
        }}
        className="w-auto! max-w-[280px]"
      >
        {speakers.map((s) => (
          <option key={s.id} value={s.id}>
            {speakerLabel(s)}
          </option>
        ))}
      </Select>
      <span className="rounded-sm bg-accent-soft px-1.5 py-0.5 text-2xs font-semibold tracking-wide text-link uppercase">
        Editing
      </span>
    </div>
  );
}

function ConfirmSpeaker({
  score,
  checked,
  onChange,
}: {
  score: number | null;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="mt-2 flex items-start gap-2 text-md">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={checked}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
      />
      <span>
        Confirm this speaker
        <span className="block text-muted">
          Machine attribution {score?.toFixed(2) ?? "unavailable"}. Choosing or confirming the speaker marks the turn
          “Confirmed by a person”; the score stays for reference.
        </span>
      </span>
    </label>
  );
}

function ConflictCallout({
  by,
  at,
  onCompare,
  onKeep,
}: {
  by: string | null;
  at: string | null;
  onCompare: () => void;
  onKeep: () => void;
}) {
  return (
    <Callout
      tone="waiting"
      role="alert"
      className="mt-2"
      title="This passage changed while you were editing"
      actions={
        <>
          <Button size="sm" onClick={onCompare}>
            Compare…
          </Button>
          <Button size="sm" variant="primary" onClick={onKeep}>
            Keep my draft
          </Button>
        </>
      }
    >
      {by ?? "Someone"} changed it{at ? ` at ${formatDateTime(at)}` : ""}. Your draft is kept until you choose.
    </Callout>
  );
}

function ReasonRow({
  idx,
  value,
  error,
  busy,
  onChange,
  onSave,
  onCancel,
  onMore,
}: {
  idx: number;
  value: string;
  error: string | null;
  busy: boolean;
  onChange: (reason: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onMore: () => void;
}) {
  return (
    <>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <div className="min-w-[220px] flex-1">
          <FieldLabel htmlFor={`reason-${String(idx)}`} required>
            Reason
          </FieldLabel>
          <TextInput
            id={`reason-${String(idx)}`}
            className="h-8"
            autoComplete="off"
            placeholder="e.g. misheard number, wrong speaker"
            value={value}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `reason-${String(idx)}-err` : undefined}
            onChange={(e) => {
              onChange(e.target.value);
            }}
          />
        </div>
        <Button size="sm" variant="primary" onClick={onSave} aria-busy={busy || undefined}>
          Save
        </Button>
        <Button size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" variant="ghost" title="Profile link, occurrence date, context and evidence" onClick={onMore}>
          More corrections…
        </Button>
      </div>
      {error ? (
        <p id={`reason-${String(idx)}-err`} className="m-0 mt-1 text-md text-attention">
          {error}
        </p>
      ) : null}
    </>
  );
}

interface Base {
  revisionId: string;
  unitId: string;
  text: string;
  speakerId: string | null;
}

interface Conflict {
  current: TurnConflict;
  by: string | null;
  at: string | null;
}

interface Choice {
  text: "saved" | "mine";
  speaker: "saved" | "mine";
}

function changesOf(d: Draft, from: Base): CorrectionChange[] {
  const changes: CorrectionChange[] = [];
  const newText = d.text.trim().split(/\s+/).join(" ");
  if (newText !== from.text) changes.push({ kind: "turn_text", text: newText });
  if (d.speakerId && (d.speakerId !== from.speakerId || d.confirm))
    changes.push({ kind: "speaker", speaker_id: d.speakerId });
  return changes;
}

function savedMessage(resolved: number): string {
  const reports = resolved > 0 ? `; ${String(resolved)} report${resolved === 1 ? "" : "s"} resolved` : "";
  return `Correction saved${reports}. Checks rerun.`;
}

function chosenDraft(draft: Draft, current: TurnConflict, choice: Choice): Draft {
  return {
    ...draft,
    text: choice.text === "saved" ? (current.text ?? draft.text) : draft.text,
    speakerId: choice.speaker === "saved" ? current.speaker_id : draft.speakerId,
  };
}

function conflictBase(current: TurnConflict): Base | null {
  if (!current.unit_id) return null;
  return {
    revisionId: current.revision_id,
    unitId: current.unit_id,
    text: current.text ?? "",
    speakerId: current.speaker_id,
  };
}

function editorKeys(cancel: () => void, save: () => void) {
  return (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      cancel();
    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      save();
    }
  };
}

function EditorError({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <Callout tone="attention" role="alert" className="mt-2">
      {error}
    </Callout>
  );
}

function EditorHint() {
  return (
    <p className="m-0 mt-1.5 text-md text-muted">
      The archived source stays unchanged. Saving reruns the wording and context checks; a speaker change also reruns
      attribution. ⌘Enter save · Esc cancel
    </p>
  );
}

function ConflictArea({
  conflict,
  compare,
  draft,
  speakers,
  onCompare,
  onKeep,
}: {
  conflict: Conflict;
  compare: Choice | null;
  draft: Draft;
  speakers: readonly SpeakerView[];
  onCompare: (choice: Choice | null) => void;
  onKeep: (d?: Draft) => void;
}) {
  return (
    <>
      <ConflictCallout
        by={conflict.by}
        at={conflict.at}
        onCompare={() => {
          onCompare({ text: "mine", speaker: "mine" });
        }}
        onKeep={() => {
          onKeep();
        }}
      />
      {compare ? (
        <CompareDialog
          conflict={conflict.current}
          draft={draft}
          speakers={speakers}
          choice={compare}
          onChoice={onCompare}
          onCancel={() => {
            onCompare(null);
          }}
          onUse={() => {
            onKeep(chosenDraft(draft, conflict.current, compare));
          }}
        />
      ) : null}
    </>
  );
}

export function TurnEditor({
  record,
  turn,
  editing,
  onDone,
  onMore,
}: {
  record: EvidenceRecord;
  turn: TurnView;
  editing: Editing;
  onDone: (message: string | null) => void;
  onMore: () => void;
}) {
  const original = turn.words.map((w) => w.text).join(" ");
  const [draft, setDraft] = useState<Draft>({ text: original, speakerId: turn.speaker_id, confirm: false, reason: "" });
  const com = communicationOf(record, turn);
  const [base, setBase] = useState<Base | null>(() =>
    com?.version && turn.unit_id
      ? { revisionId: com.version.revision_id, unitId: turn.unit_id, text: original, speakerId: turn.speaker_id }
      : null,
  );
  const [resolves, setResolves] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [compare, setCompare] = useState<Choice | null>(null);
  const [busy, setBusy] = useState(false);
  const text = useRef<HTMLTextAreaElement>(null);
  const problems = record.problems.filter((p) => turn.problems.includes(p.id));

  const comId = com?.id ?? null;
  useEffect(() => {
    text.current?.focus();
    text.current?.closest("article")?.scrollIntoView({ block: "nearest" });
    if (comId)
      editing.acquire(comId).catch((e: unknown) => {
        setError(isApiError(e) ? e.message : "Couldn't start editing");
      });
  }, [comId, editing]);

  const submit = async (override?: Base, d: Draft = draft) => {
    const from = override ?? base;
    if (!com || !from) return;
    const changes = changesOf(d, from);
    if (changes.length === 0) {
      setError("Nothing changed. Edit the text, choose a speaker or confirm this speaker.");
      return;
    }
    if (d.reason.trim().length === 0) {
      setReasonError("Add a short reason so others can follow the change.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await editing.save({
        communicationId: com.id,
        baseRevisionId: from.revisionId,
        unitId: from.unitId,
        reason: d.reason.trim(),
        changes,
        resolves,
      });
      onDone(savedMessage(resolves.length));
    } catch (e) {
      const current = isApiError(e) ? turnConflictSchema.safeParse(e.current) : null;
      if (isApiError(e) && current?.success) setConflict({ current: current.data, by: e.changedBy, at: e.changedAt });
      else setError(isApiError(e) ? e.message : "Couldn't save the correction");
    } finally {
      setBusy(false);
    }
  };

  const keepMine = (d: Draft = draft) => {
    const next = conflict ? conflictBase(conflict.current) : null;
    if (!next) return;
    setBase(next);
    setConflict(null);
    setCompare(null);
    void submit(next, d);
  };

  const cancel = () => {
    void editing.release();
    onDone(null);
  };
  const speakers = record.speakers;
  const uncertain = turn.attribution.uncertain;
  return (
    <div
      className="rounded-md"
      onKeyDown={editorKeys(cancel, () => {
        void submit();
      })}
    >
      <SpeakerRow
        speakers={speakers}
        value={draft.speakerId}
        onChange={(speakerId) => {
          setDraft({ ...draft, speakerId });
        }}
      />
      <TextArea
        ref={text}
        aria-label="Text of this turn"
        spellCheck={false}
        value={draft.text}
        onChange={(e) => {
          setDraft({ ...draft, text: e.target.value });
        }}
        className="text-base"
      />
      {uncertain ? (
        <ConfirmSpeaker
          score={turn.attribution.value}
          checked={draft.confirm}
          onChange={(confirm) => {
            setDraft({ ...draft, confirm });
          }}
        />
      ) : null}
      <OpenReports problems={problems} picked={resolves} onPick={setResolves} />
      {conflict ? (
        <ConflictArea
          conflict={conflict}
          compare={compare}
          draft={draft}
          speakers={speakers}
          onCompare={setCompare}
          onKeep={(next) => {
            if (next) setDraft(next);
            keepMine(next);
          }}
        />
      ) : null}
      <EditorError error={error} />
      <ReasonRow
        idx={turn.idx}
        value={draft.reason}
        error={reasonError}
        busy={busy}
        onChange={(reason) => {
          setDraft({ ...draft, reason });
          setReasonError(null);
        }}
        onSave={() => void submit()}
        onCancel={cancel}
        onMore={onMore}
      />
      <EditorHint />
    </div>
  );
}

function CompareDialog({
  conflict,
  draft,
  speakers,
  choice,
  onChoice,
  onCancel,
  onUse,
}: {
  conflict: TurnConflict;
  draft: Draft;
  speakers: readonly SpeakerView[];
  choice: Choice;
  onChoice: (c: Choice) => void;
  onCancel: () => void;
  onUse: () => void;
}) {
  const name = (id: string | null) => speakers.find((s) => s.id === id)?.display ?? "Unattributed";
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      title="Compare with the saved version"
      onSubmit={onUse}
      footer={
        <>
          <Button onClick={onCancel}>Back</Button>
          <Button type="submit" variant="primary">
            Save with these choices
          </Button>
        </>
      }
    >
      <DialogText>
        Choose per field between the saved value and your draft. Your choices are saved on top of the newer version.
      </DialogText>
      <RadioCards
        label="Text"
        value={choice.text}
        onChange={(v) => {
          onChoice({ ...choice, text: v });
        }}
        options={[
          { value: "saved", label: "Saved", description: conflict.text ?? "" },
          { value: "mine", label: "Your draft", description: draft.text },
        ]}
      />
      <RadioCards
        label="Speaker"
        value={choice.speaker}
        onChange={(v) => {
          onChoice({ ...choice, speaker: v });
        }}
        options={[
          { value: "saved", label: "Saved", description: name(conflict.speaker_id) },
          { value: "mine", label: "Your draft", description: name(draft.speakerId) },
        ]}
      />
    </Dialog>
  );
}
